import { useEffect, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Radio } from "lucide-react";

const HERO_FALLBACK =
  "https://images.unsplash.com/photo-1543372993-db3d6409e431?q=85&w=2400&auto=format&fit=crop";

const Line = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const pills = [
  { label: "Tonight", filter: "Tonight" },
  { label: "This Weekend", filter: "Weekend" },
  { label: "Live Music", filter: "Live" },
  { label: "Clubs", filter: "Club" },
  { label: "Food & Markets", filter: "Food" },
];

export default function Hero({ entered, onEnter, city }) {
  const [now, setNow] = useState("");
  const timezone = city?.timezone || "America/New_York";

  useEffect(() => {
    const tick = () =>
      setNow(
        new Intl.DateTimeFormat("en-US", {
          timeZone: timezone,
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        }).format(new Date())
      );
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [timezone]);

  const heroImg = city?.hero_image || HERO_FALLBACK;
  const cityName = city?.name || "Philadelphia";
  const coords = city?.coords || "39.9526°N 75.1652°W";

  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 180]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.25]);

  return (
    <section id="top" className="relative flex min-h-screen flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: yBg }} className="absolute inset-0">
        <AnimatePresence mode="wait">
          <motion.img
            key={heroImg}
            src={heroImg}
            alt={`${cityName} glowing at night`}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="h-[115%] w-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-[#07080B] via-[#07080B]/55 to-[#07080B]/25" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(7,8,11,0.55)_100%)]" />
      </motion.div>

      <AnimatePresence>
        {entered && (
          <>
            <motion.div
              key="gate-l"
              initial={{ x: 0 }}
              animate={{ x: "-100%" }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.9, ease: [0.83, 0, 0.17, 1] }}
              className="pointer-events-none fixed inset-y-0 left-0 z-40 w-1/2 bg-[#07080B]"
            />
            <motion.div
              key="gate-r"
              initial={{ x: 0 }}
              animate={{ x: "100%" }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.9, ease: [0.83, 0, 0.17, 1] }}
              className="pointer-events-none fixed inset-y-0 right-0 z-40 w-1/2 bg-[#07080B]"
            />
          </>
        )}
      </AnimatePresence>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-14 pt-36 sm:px-8">
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <Line delay={0.2}>
            <span
              data-testid="hero-coordinates"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/50 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.25em] text-zinc-300 backdrop-blur-md"
            >
              <Radio className="h-3 w-3 animate-pulse-glow text-neon-lime" />
              {coords} — {cityName}
            </span>
          </Line>
          <Line delay={0.3}>
            <span
              data-testid="hero-live-clock"
              className="inline-flex items-center gap-2 rounded-full border border-neon-lime/30 bg-neon-lime/10 px-4 py-1.5 font-mono text-xs tracking-[0.25em] text-neon-lime"
            >
              {now} LOCAL
            </span>
          </Line>
        </div>

        <h1 className="font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tighter sm:text-5xl lg:text-6xl xl:text-7xl" data-testid="hero-title">
          <Line delay={0.45}>Where the city</Line>
          <Line delay={0.58}>
            <span className="text-neon-lime text-glow-lime">never</span> sleeps,
          </Line>
          <Line delay={0.71}>it just changes</Line>
          <Line delay={0.84}>
            <span className="text-neon-cyan text-glow-cyan">neighborhoods.</span>
          </Line>
        </h1>

        <Line delay={1.05} className="mt-7 max-w-xl">
          <p className="text-sm leading-relaxed text-zinc-300 sm:text-base" data-testid="hero-subtitle">
            {city?.tagline ||
              "Every event, party, and late-night plate — curated for out-of-towners who want the real city, not the brochure."}
          </p>
        </Line>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => onEnter()}
            data-testid="hero-enter-button"
            className="group inline-flex items-center gap-3 rounded-full bg-neon-lime px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.25em] text-black transition-all hover:-translate-y-1 hover:shadow-[0_0_40px_rgba(34,197,94,0.55)]"
          >
            Enter the City
            <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-1" />
          </button>
          <div className="flex flex-wrap gap-2">
            {pills.map((p) => (
              <button
                key={p.filter}
                onClick={() => onEnter(p.filter)}
                data-testid={`hero-filter-${p.filter.toLowerCase()}`}
                className="rounded-full border border-white/15 bg-black/50 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-300 backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-neon-cyan/60 hover:text-neon-cyan"
              >
                {p.label}
              </button>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
